import { createContext, useEffect, useState, use, type RefObject } from 'react';

import type { MediaState, MediaStore, MediaProviderProps } from '.';

const MediaContext = createContext<MediaStore | null>(null);

export function MediaProvider(mediaProviderProps: MediaProviderProps) {
  const { children, store } = mediaProviderProps;
  return <MediaContext value={store}>{children}</MediaContext>;
}

export function useMedia(): MediaStore {
  const context: MediaStore | null = use(MediaContext);
  if (!context) {
    throw new Error('useMedia must be used within MediaProvider');
  }
  return context;
}

export function useMediaStore(element: RefObject<HTMLMediaElement | null>, container?: RefObject<HTMLElement | null>): MediaStore {
  const [state, setState] = useState<MediaState>({
    isPlaying: false,
    isMuted: false,
    isPaused: true,
    isBuffering: false,
    currentTime: 0,
    duration: 0,
    volume: 1,
    buffered: 0,
    error: null,
    isFullscreen: false,
  });

  const currentPercentage: number = (state.currentTime / state.duration) * 100;
  const bufferedPercentage: number = (state.buffered / state.duration) * 100;

  const formatTime = (time: number): string => {
    if (!Number.isFinite(time) || time < 0) {
      return '--:--';
    }
    const totalSeconds: number = Math.floor(time);
    const minutes: number = Math.floor(totalSeconds / 60);
    const seconds: number = totalSeconds % 60;
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const updateState = (updates: Partial<typeof state>) => {
    setState((prev) => ({ ...prev, ...updates }));
  };

  const pause = () => {
    if (element.current) {
      element.current.pause();
      updateState({ isPlaying: false, isPaused: true });
    }
  };

  const play = async () => {
    if (element.current) {
      // Dispatch an event to stop other players
      window.dispatchEvent(new CustomEvent('media-play', { detail: element.current }));
      try {
        await element.current.play();
        updateState({ isPlaying: true, isPaused: false });
      } catch (error) {
        console.error('Play failed:', error);
      }
    }
  };

  const togglePlay = async () => {
    if (element.current) {
      if (state.isPlaying) {
        pause();
      } else {
        await play();
      }
    }
  };

  const toggleMute = () => {
    if (element.current) {
      element.current.muted = !state.isMuted;
      updateState({ isMuted: !state.isMuted });
    }
  };

  const volumeChange = (value: number) => {
    const newVolume: number = Math.min(1, Math.max(0, value));
    updateState({
      volume: newVolume,
      isMuted: newVolume === 0,
    });
    if (element.current) {
      element.current.volume = newVolume;
      element.current.muted = newVolume === 0;
    }
  };

  const seek = (value: number) => {
    const newTime: number = Math.min(Math.max(0, value), state.duration);
    updateState({ currentTime: newTime });
    if (element.current) {
      element.current.currentTime = newTime;
    }
  };

  const skip = (seconds: number) => {
    if (element.current) {
      const newTime = Math.max(0, Math.min(state.duration, state.currentTime + seconds));
      element.current.currentTime = newTime;
      updateState({ currentTime: newTime });
    }
  };

  const updateBufferedProgress = () => {
    if (element.current && element.current.buffered.length > 0) {
      const bufferedEnd: number = element.current.buffered.end(element.current.buffered.length - 1);
      updateState({ buffered: bufferedEnd });
    }
  };

  const enterFullscreen = async () => {
    if (container?.current && !document.fullscreenElement) {
      try {
        await container.current.requestFullscreen();
      } catch (error) {
        console.error('Fullscreen failed:', error);
      }
    }
  };

  const exitFullscreen = async () => {
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen();
      } catch (error) {
        console.error('Exit fullscreen failed:', error);
      }
    }
  };

  const toggleFullscreen = async () => {
    if (state.isFullscreen) {
      await exitFullscreen();
    } else {
      await enterFullscreen();
    }
  };

  const setVolume = (valueOrUpdater: number | ((prev: number) => number)) => {
    setState((prev) => {
      const newVolume: number = typeof valueOrUpdater === 'function' ? valueOrUpdater(prev.volume) : valueOrUpdater;
      const clampedVolume: number = Math.min(1, Math.max(0, newVolume));
      if (element.current) {
        element.current.volume = clampedVolume;
        element.current.muted = clampedVolume === 0;
      }
      return {
        ...prev,
        volume: clampedVolume,
        isMuted: clampedVolume === 0,
      };
    });
  };

  // Listen for the event and pause if not the source
  useEffect(() => {
    const controller = new AbortController();

    const handler = (e: Event) => {
      const target = (e as CustomEvent).detail;
      if (element.current && element.current !== target) {
        element.current.pause();
      }
    };
    window.addEventListener('media-play', handler, {
      signal: controller.signal,
    });
    return () => controller.abort();
  }, []);

  useEffect(() => {
    const controller = new AbortController();
    const mediaElement = element.current;
    if (!mediaElement) return;

    document.addEventListener('fullscreenchange', () => updateState({ isFullscreen: !!document.fullscreenElement }), {
      signal: controller.signal,
    });

    const events: Record<string, EventListener> = {
      loadedmetadata: () => {
        if (Number.isFinite(mediaElement.duration) && mediaElement.duration > 0) {
          updateState({ duration: mediaElement.duration });
        }
      },
      durationchange: () => {
        if (Number.isFinite(mediaElement.duration) && mediaElement.duration > 0) {
          updateState({ duration: mediaElement.duration });
        }
      },
      timeupdate: () => {
        updateState({ currentTime: mediaElement.currentTime });
        updateBufferedProgress();
      },
      progress: updateBufferedProgress,
      waiting: () => updateState({ isBuffering: true }),
      canplay: () => updateState({ isBuffering: false }),
      canplaythrough: () => updateState({ isBuffering: false }),
      play: () => updateState({ isPlaying: true }),
      pause: () => updateState({ isPlaying: false }),
      volumechange: () =>
        updateState({
          volume: mediaElement.volume,
          isMuted: mediaElement.muted,
        }),
      ended: () => updateState({ isPlaying: false }),
      error: () => updateState({ error: mediaElement.error, isBuffering: false }),
    };

    Object.entries(events).forEach(([event, handler]) => {
      mediaElement.addEventListener(event, handler, {
        signal: controller.signal,
      });
    });

    return () => controller.abort();
  }, []);

  return {
    ...state,
    currentPercentage,
    bufferedPercentage,
    element,
    container,
    play,
    pause,
    togglePlay,
    skip,
    formatTime,
    toggleMute,
    seek,
    volumeChange,
    setVolume,
    toggleFullscreen,
    enterFullscreen,
    exitFullscreen,
  };
}
